import {
  toWSDateString,
  useWSModal,
  useWSSnackbar,
  WSButton,
  WSButtons,
  WSElement,
  WSElementProps,
  WSText
} from "@wingspanhq/fe-component-library";
import { Currency } from "@wingspanhq/wingspan-api-sdk";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import React from "react";
import { queriesTaxWithholdings } from "@query";
import { IdempotencyKey } from "@utils/idempotencyKey";

export type ModalCancelPendingWithholdingProps = {
  taxPaymentId: string;
  amount: number;
  currency: Currency;
  date: Date | string;
  onClose: (cancelled: boolean) => void;
} & WSElementProps;

export const ModalCancelPendingWithholding: React.FC<
  ModalCancelPendingWithholdingProps
> = props => {
  const { onClose, taxPaymentId, amount, currency, date, ...elementProps } =
    props;
  const [cancelTaxPayment, cancelTaxPaymentMeta] =
    queriesTaxWithholdings.cancelTaxPayment.useMutation();
  const snackbar = useWSSnackbar();

  return (
    <WSElement {...elementProps}>
      <WSText mb="2XL">
        Are you sure you want to cancel this scheduled payment to IRS of{" "}
        {formatMoney(amount, currency, { showSign: false })} from{" "}
        {toWSDateString(date, "monthDayYear")}?
      </WSText>
      <WSText mb="2XL">Once confirmed, this action cannot be undone.</WSText>

      <WSButtons format="modal">
        <WSButton
          destructive
          onAsyncClick={async () => {
            const idempotencyKey = new IdempotencyKey({ taxPaymentId });

            await cancelTaxPayment(
              { taxPaymentId, idempotencyKey: idempotencyKey.value },
              {
                onSuccess: () => {
                  idempotencyKey.destroy();

                  snackbar.openSnackbar({
                    icon: { name: "check" },
                    message: "Pending transaction cancelled successfully"
                  });

                  onClose(true);
                }
              }
            );
          }}
          loading={cancelTaxPaymentMeta.isLoading}
        >
          Cancel transaction
        </WSButton>
        <WSButton.Tertiary onClick={() => onClose(false)}>
          Keep transaction
        </WSButton.Tertiary>
      </WSButtons>
    </WSElement>
  );
};

export const useModalCancelPendingWithholding = () =>
  useWSModal(ModalCancelPendingWithholding, {
    title: "Cancel pending transaction",
    size: "S"
  });
